import { AfterViewInit, Component, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { EnvironmentService } from '@app/core/services/environment.service';
import { OidcAuthService } from '@app/core/services/oidc-auth.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { TelerikScriptLoaderService } from './services/telerik-script-loader.service';
import { ReportDesignerService } from './services/report-designer.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';

declare const $: any;
declare const kendo: any;

@Component({
    selector: 'app-report-designer',
    templateUrl: './report-designer.component.html',
    styleUrls: ['./report-designer.component.scss']
})
export class ReportDesignerComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() reportId?: string;
    @Input() reportType?: string;
    @Input() reportName?: string;

    @ViewChild('designerContainer') designerContainer: ElementRef<HTMLElement>;

    public errorMessage: string | null = null;

    public readonly detailsFormId = 'frm_Rd1nAeRzRzEDRI';
    public readonly statusFormId = 'frm_sun2iJihgj9XXj';

    public report: any;
    public loadingReport = false;

    public detailsFormData: any;
    public getDetailsFormData = false;
    public detailsFormValid = false;
    public detailsFormPristine = true;
    public savingDetails = false;

    public statusFormData: any;
    public getStatusFormData = false;
    public statusFormValid = false;
    public statusFormPristine = true;
    public savingStatus = false;

    public activeSettingsTab: 'details' | 'access' | 'status' = 'details';
    public securityDirty = false;
    public assignedRoleCount: number | null = null;
    public layoutDirty = false;

    private reportRequest: Subscription | null = null;

    private readonly cdnBase = 'https://reporting.cdn.telerik.com/20.2.26.812/';
    private readonly fallbackDesignerUrl = 'https://demos.telerik.com/reporting/api/reportdesigner/';
    private designer: any;
    private originalFetch: typeof window.fetch | null = null;
    private originalFocus: typeof HTMLElement.prototype.focus | null = null;
    private paramMapSubscription: Subscription | null = null;
    private designerResizeObserver: ResizeObserver | null = null;
    private designerResizeFrame: number | null = null;
    private layoutDirtyPoll: ReturnType<typeof setInterval> | null = null;
    // Set while we navigate ourselves (Save As keeping the URL in sync) so the guard doesn't prompt.
    private skipDeactivateCheck = false;

    constructor(
        private envService: EnvironmentService,
        private route: ActivatedRoute,
        private router: Router,
        private oidcAuthService: OidcAuthService,
        private scriptLoader: TelerikScriptLoaderService,
        private reportDesignerService: ReportDesignerService,
        private snackbarService: SnackbarService,
        private ngZone: NgZone,
        private overlayService: OverlayService,
        private dialog: MatDialog,
        private translate: TranslateService,
    ) { }

    ngOnInit(): void {
        // The designer's own bootstrap calls (configuration, deniedPermissions, designerresources,
        // etc.) are plain window.fetch calls made by webReportDesigner.min.js itself - they never go
        // through viewerInitializing, which only covers the embedded print-preview viewer. Per
        // Telerik's own guidance, the only way to authenticate them is to intercept fetch directly:
        // https://docs.telerik.com/reporting/knowledge-base/add-authentication-header-webreportdesigner
        this.overrideFetchForDesigner();
        this.preventDesignerFocusScroll();

        this.loadScripts().then(() => {
            // Angular reuses this component instance across Reports/Designer/:id navigations
            // (same route path, only the param changes), so reading route.snapshot once in
            // ngOnInit would leave the widget permanently bound to whichever id it saw first.
            // Subscribing to paramMap re-initializes the widget whenever the id actually changes.
            this.paramMapSubscription = this.route.paramMap.subscribe((params) => {
                const routeReportId = params.get('id') || undefined;
                const routeReportType = params.get('type') || this.reportType;

                if (routeReportId === this.reportId && this.designer) {
                    return;
                }

                this.reportId = routeReportId;
                this.reportType = routeReportType;

                this.loadReport();
                this.initializeDesigner();
            });
        });
    }

    // The designer's Kendo splitter only re-measures itself on window resize, so the page layout
    // switching columns (or the settings column appearing once the report loads) leaves its panes
    // sized for the old width and the toolbox/properties areas get clipped. Watching the container
    // itself and asking Kendo to re-layout keeps the panes fitted to whatever space it actually has.
    ngAfterViewInit(): void {
        this.startLayoutDirtyPoll();

        if (typeof ResizeObserver === 'undefined' || !this.designerContainer) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this.designerResizeObserver = new ResizeObserver(() => {
                if (this.designerResizeFrame !== null) {
                    cancelAnimationFrame(this.designerResizeFrame);
                }
                this.designerResizeFrame = requestAnimationFrame(() => {
                    this.designerResizeFrame = null;
                    this.resizeDesigner();
                });
            });
            this.designerResizeObserver.observe(this.designerContainer.nativeElement);
        });
    }

    private resizeDesigner(): void {
        if (!this.designer || typeof kendo === 'undefined' || typeof kendo.resize !== 'function') {
            return;
        }

        kendo.resize($('#webReportDesigner'), true);
    }

    private initializeDesigner(): void {
        if (this.designer && typeof this.designer.destroy === 'function') {
            this.designer.destroy();
        }

        const designerElement = document.getElementById('webReportDesigner');
        if (designerElement) {
            designerElement.innerHTML = '';
        }

        // The widget's own "Restore reports" preference persists the last-opened report id in
        // localStorage (an unprefixed key shared by every widget instance on this origin) and its
        // getDocumentId() silently reopens that id instead of the report: option below whenever
        // one is present - clearing it here ensures navigating to a different report always opens
        // that report, not whatever report was last open in any tab.
        try {
            localStorage.removeItem('LastOpenedReport');
        } catch {
            // localStorage unavailable (e.g. private browsing) - the widget's own restore logic
            // would no-op here too, so there's nothing for us to clear.
        }

        const onViewerInitializing = (e: any, args: any) => {
            args.reportViewerOptions.authenticationToken = this.oidcAuthService.authorizationHeaderValue; // same access token used for other app API calls
        };

        // The designer's REST layer requires a file extension on every report uri (its own
        // "reports/exists" action 400s with "No file extension provided" otherwise) - the
        // backend strips it back off before looking the report up by its real DisplayId.
        this.designer = $('#webReportDesigner').telerik_WebReportDesigner({
            serviceUrl: `${this.envService.env.apiUrl}Reports/Designer/`,
            report: this.reportId ? `${this.reportId}.trdp` : this.reportId,
            viewerInitializing: onViewerInitializing
        }).data('telerik_WebReportDesigner');
    }

    // The list/detail endpoints may resolve Name to a single string in the viewer's culture, or
    // return every localization - show the one matching the report's culture when it's an array.
    get reportDisplayName(): string {
        const name = this.report?.name;
        if (Array.isArray(name)) {
            return (name.find(n => n.culture === this.report?.culture?.id) ?? name[0])?.text ?? '';
        }
        return name ?? '';
    }

    get statusName(): string {
        return this.report?.status?.name ?? '';
    }

    loadReport(): void {
        this.reportRequest?.unsubscribe();

        if (!this.reportId) {
            this.report = null;
            return;
        }

        this.loadingReport = true;

        this.reportRequest = this.reportDesignerService.getReport(this.reportId)
            .pipe(finalize(() => this.loadingReport = false))
            .subscribe({
                next: (report: any) => {
                    this.report = report;
                    this.setFormData(report);
                },
                error: () => {
                    this.snackbarService.openSnackBar('Unable to load report details', 'clear', 'warn');
                }
            });
    }

    private setFormData(report: any): void {
        this.detailsFormData = {
            name: report.name,
            description: report.description ?? null,
            reportTypeId: report.type?.id ?? null,
            reportDataSourceId: report.reportDataSource?.id ?? null,
            cultureId: report.culture?.id ?? null,
        };
        this.detailsFormPristine = true;

        this.statusFormData = {
            reportStatusId: report.status?.id ?? null,
        };
        this.statusFormPristine = true;
    }

    saveDetails(formData: any): void {
        this.overlayService.show();
        const cultureId = this.toId(formData.cultureId) ?? this.report?.culture?.id ?? 'en-US';

        const payload = {
            // The name field may be a localization field (array of { culture, text }) or a plain string.
            name: Array.isArray(formData.name)
                ? formData.name
                : [{ culture: cultureId, text: formData.name }],
            description: formData.description || null,
            reportTypeId: this.toId(formData.reportTypeId),
            reportDataSourceId: this.toId(formData.reportDataSourceId),
            cultureId: cultureId,
        };

        this.savingDetails = true;

        this.reportDesignerService.updateReport(this.reportId, payload)
            .pipe(finalize(() => {
                this.savingDetails = false;
                this.overlayService.hide();
            }))
            .subscribe({
                next: () => {
                    this.snackbarService.openSnackBar('Report details saved successfully', 'clear', 'success');
                    this.refreshReport();
                },
                error: () => {
                    this.detailsFormPristine = false;
                    this.snackbarService.openSnackBar('Unable to save report details', 'clear', 'warn');
                }
            });
    }

    saveStatus(formData: any): void {
        this.overlayService.show();
        const reportStatusId = this.toId(formData.reportStatusId);
        if (!reportStatusId) {
            return;
        }

        this.savingStatus = true;

        this.reportDesignerService.changeReportStatus(this.reportId, reportStatusId)
            .pipe(finalize(() => {
                this.savingStatus = false;
                this.overlayService.hide();
            }))
            .subscribe({
                next: () => {
                    this.snackbarService.openSnackBar('Report status updated successfully', 'clear', 'success');
                    this.refreshReport();
                },
                error: () => {
                    this.statusFormPristine = false;
                    this.snackbarService.openSnackBar('Unable to update report status', 'clear', 'warn');
                }
            });
    }

    // Re-fetches the report after a save so the header (name, status badge) reflects it, without
    // toggling loadingReport - that would tear down and rebuild both forms mid-edit.
    private refreshReport(): void {
        this.reportDesignerService.getReport(this.reportId).subscribe((report: any) => {
            this.report = report;
        });
    }

    // Form generator dropdowns can emit either the raw id or the selected { id, name } object.
    private toId(value: any): string {
        return value && typeof value === 'object' ? value.id : value;
    }

    goToAdmin(): void {
        this.router.navigate(['/Reports/Admin']);
    }

    get hasUnsavedChanges(): boolean {
        return this.layoutDirty
            || !this.detailsFormPristine
            || !this.statusFormPristine
            || this.securityDirty;
    }

    // Called by CanDeactivateGuard - covers the Close button and any in-app navigation away.
    canDeactivate(): Observable<boolean> {
        this.layoutDirty = this.readLayoutDirty();

        if (this.skipDeactivateCheck || !this.hasUnsavedChanges) {
            return of(true);
        }

        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            text: this.translate.instant('UnsavedChangesMessage')
        };

        return this.dialog.open(ConfirmDialogComponent, dialogConfig)
            .afterClosed()
            .pipe(map(confirmed => confirmed === true));
    }

    // The designer's own beforeunload prompt (promptOnDiscardingModifiedReport) only covers the
    // layout - this extends the browser's refresh/close warning to the settings tabs too.
    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (!this.detailsFormPristine || !this.statusFormPristine || this.securityDirty) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    // Telerik exposes no public API or event for unsaved layout changes. Internally each open
    // document's design surface carries an IsModified flag (the widget's own Save command and
    // beforeunload prompt read it through IDesignSurfaceManager.GetLoadedDocuments), so read that.
    // Undocumented and minified - re-check after upgrading the Telerik version in loadScripts().
    // Any failure reads as "not modified" so a changed internal API never blocks navigation.
    private readLayoutDirty(): boolean {
        try {
            const manager = this.designer?.shell?.getServiceByName('IDesignSurfaceManager');
            const documents = manager?.GetLoadedDocuments;
            return Array.isArray(documents) && documents.some((doc: any) => doc?.IsModified === true);
        } catch {
            return false;
        }
    }

    // There's no change event to subscribe to either, so poll - cheap, and kept outside the zone
    // so it only triggers change detection when the flag actually flips.
    private startLayoutDirtyPoll(): void {
        this.ngZone.runOutsideAngular(() => {
            this.layoutDirtyPoll = setInterval(() => {
                const dirty = this.readLayoutDirty();
                if (dirty !== this.layoutDirty) {
                    this.ngZone.run(() => this.layoutDirty = dirty);
                }
            }, 1000);
        });
    }

    ngOnDestroy(): void {
        this.paramMapSubscription?.unsubscribe();
        this.reportRequest?.unsubscribe();
        this.designerResizeObserver?.disconnect();
        if (this.layoutDirtyPoll !== null) {
            clearInterval(this.layoutDirtyPoll);
        }
        if (this.designerResizeFrame !== null) {
            cancelAnimationFrame(this.designerResizeFrame);
        }

        if (this.designer && typeof this.designer.destroy === 'function') {
            this.designer.destroy();
        }

        const designerElement = document.getElementById('webReportDesigner');
        if (designerElement) {
            designerElement.innerHTML = '';
        }

        this.restoreFetch();
        this.restoreFocus();
    }

    // Once a report loads, the widget selects its root item and calls a plain .focus() on the
    // design surface overlay (jQuery's .focus() ends up here too), which makes the browser scroll
    // the page down to the designer. Focusing with preventScroll for anything inside the widget
    // keeps keyboard focus behaviour intact without the page jumping - the element being focused
    // is always one the user can already see or just clicked.
    private preventDesignerFocusScroll(): void {
        const originalFocus = HTMLElement.prototype.focus;
        this.originalFocus = originalFocus;

        HTMLElement.prototype.focus = function (this: HTMLElement, options?: FocusOptions) {
            if (this.closest('#webReportDesigner')) {
                return originalFocus.call(this, { ...options, preventScroll: true });
            }
            return originalFocus.call(this, options);
        };
    }

    private restoreFocus(): void {
        if (this.originalFocus) {
            HTMLElement.prototype.focus = this.originalFocus;
            this.originalFocus = null;
        }
    }

    private overrideFetchForDesigner(): void {
        const originalFetch = window.fetch.bind(window);
        this.originalFetch = originalFetch;

        const serviceUrl = `${this.envService.env.apiUrl}Reports/Designer/`;

        window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
            const url = typeof input === 'string' ? input : input.toString();

            if (url.startsWith(serviceUrl)) {
                const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
                headers.set('Authorization', this.oidcAuthService.authorizationHeaderValue);
                headers.set('TenantId', this.oidcAuthService.getTenantId()); // same header AddHeaderInterceptor attaches to HttpClient requests
                // SaveResourceModel (the widget's Save/Save As payload) only carries the target
                // name - nothing identifies which report was open before Save As was invoked.
                // Stamping the currently-open report's id on every designer request gives
                // ReportDesignerDefinitionStorage.SaveAsync the one signal it needs to know what
                // to copy from when the target name turns out to be a new (Save As) document.
                if (this.reportId) {
                    headers.set('SourceReportId', this.reportId);
                }
                init = { ...init, headers };

                const method = (init.method ?? 'GET').toUpperCase();
                if (method !== 'GET') {
                    return originalFetch(input, init).then((response) => this.captureSavedReportId(response));
                }
            }

            return originalFetch(input, init);
        };
    }

    // Save and Save As both come back through here as a ResourceFileModel { uri: "<id>.trdp", ... } -
    // for Save As specifically, that id is the newly created report (see
    // ReportDesignerDefinitionStorage.SaveAsync), which the widget itself starts tracking as the
    // open document but which the route/URL still doesn't reflect. Reading the response body (via
    // a clone, so the widget's own handling of it is untouched) and navigating whenever the id
    // differs keeps the URL in sync without disturbing the designer instance - the paramMap
    // subscription in ngOnInit no-ops once this.reportId already matches the new route param.
    private captureSavedReportId(response: Response): Response {
        if (response.ok) {
            response.clone().json().then((body) => {
                const uri = typeof body?.uri === 'string' ? body.uri : undefined;
                const savedReportId = uri?.endsWith('.trdp') ? uri.slice(0, -'.trdp'.length) : undefined;

                if (savedReportId && savedReportId !== this.reportId) {
                    this.reportId = savedReportId;
                    this.loadReport();

                    this.skipDeactivateCheck = true;
                    this.ngZone.run(() => this.router.navigate(['Reports', 'Designer', savedReportId], { replaceUrl: true }))
                        .finally(() => this.skipDeactivateCheck = false);
                }
            }).catch(() => {
                // Most designer responses aren't a ResourceFileModel (report bytes, type
                // schemas, configuration, ...) - not JSON, or JSON without a matching shape.
                // Nothing to do with those here.
            });
        }

        return response;
    }

    private restoreFetch(): void {
        if (this.originalFetch) {
            window.fetch = this.originalFetch;
            this.originalFetch = null;
        }
    }

    private get serviceUrl(): string {
        const explicitUrl = this.envService?.env?.reportDesignerApiUrl;
        if (explicitUrl) {
            return explicitUrl.replace(/\/$/, '') + '/';
        }

        const configuredUrl = (this.envService?.env?.apiUrl ?? '').replace(/\/$/, '');

        return this.fallbackDesignerUrl;
    }

    private loadScripts(): Promise<void> {
        return this.scriptLoader.loadScript('https://code.jquery.com/jquery-3.7.1.min.js')
        .then(() => {
            // The designer only falls back to fetching its bundled jQuery UI copy via
            // serviceUrl (designerresources/ext_js/jquery.ui-*.min.js) when window.jQuery.ui
            // isn't already present - that fallback path can't carry our auth/tenant headers
            // (it's a plain <script> tag), so it 404s against this app's tenant-gated backend.
            // Preloading the same jQuery UI version from its own CDN avoids that path entirely.
            this.scriptLoader.loadStyle('https://code.jquery.com/ui/1.14.1/themes/base/jquery-ui.min.css');
            return this.scriptLoader.loadScript('https://code.jquery.com/ui/1.14.1/jquery-ui.min.js');
        })
        .then(() => this.scriptLoader.loadScript('https://reporting.cdn.telerik.com/20.2.26.812/js/webReportDesigner.kendo.min.js'))
        .then(() => this.scriptLoader.loadScript('https://reporting.cdn.telerik.com/20.2.26.812/js/telerikReportViewer.min.js'))
        .then(() => this.scriptLoader.loadScript('https://reporting.cdn.telerik.com/20.2.26.812/js/webReportDesigner.min.js'));
    }

    //   private async loadDesigner(): Promise<void> {
    //     try {
    //       await this.loadScripts();

    //       const element = document.getElementById('webReportDesigner');

    //       if (!element) {
    //         this.errorMessage = 'Designer container not found.';
    //         this.isLoading = false;
    //         return;
    //       }

    //       const designerConfig: any = {
    //         serviceUrl: this.serviceUrl,
    //       };

    //       if (this.reportId) {
    //         designerConfig.report = this.reportId;
    //       }

    //       if (this.reportType) {
    //         designerConfig.reportType = this.reportType;
    //       }

    //       this.designer = $('#webReportDesigner').telerik_WebReportDesigner(designerConfig).data('telerik_WebDesigner');
    //       this.isLoading = false;
    //     } catch (error) {
    //       const serviceUrl = this.serviceUrl;
    //       console.error('Failed to load Telerik report designer scripts.', error);
    //       this.errorMessage = `The Telerik report designer could not be loaded. Endpoint: ${serviceUrl}. Confirm the Reports API endpoint is available and authenticated.`;
    //       this.isLoading = false;
    //     }
    //   }

    //   private loadScripts(): Promise<void> {
    //     const existingJQuery = (window as any).jQuery || (window as any).$;
    //     const scripts = [
    //       'https://code.jquery.com/jquery-3.7.1.min.js',
    //       `${this.telerikCdnBase}webReportDesigner.kendo.min.js`,
    //       `${this.telerikCdnBase}telerikReportViewer.min.js`,
    //       `${this.telerikCdnBase}webReportDesigner.min.js`
    //     ];

    //     const loadedScripts = (window as any).__telerikDesignerScripts || [];
    //     const missingScripts = scripts.filter((script) => !loadedScripts.includes(script));

    //     if (existingJQuery && !missingScripts.includes('https://code.jquery.com/jquery-3.7.1.min.js')) {
    //       return Promise.resolve();
    //     }

    //     if (missingScripts.length === 0) {
    //       return Promise.resolve();
    //     }

    //     return missingScripts.reduce((promise, scriptUrl) => {
    //       return promise.then(() => this.loadScript(scriptUrl));
    //     }, Promise.resolve()).then(() => {
    //       (window as any).__telerikDesignerScripts = [...loadedScripts, ...missingScripts];
    //     });
    //   }

    //   private loadScript(src: string): Promise<void> {
    //     return new Promise((resolve, reject) => {
    //       const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;

    //       if (existing) {
    //         if (existing.dataset.loaded === 'true') {
    //           resolve();
    //           return;
    //         }

    //         existing.onload = () => {
    //           existing.dataset.loaded = 'true';
    //           resolve();
    //         };
    //         existing.onerror = () => reject(new Error(`Script failed to load: ${src}`));
    //         return;
    //       }

    //       const script = document.createElement('script');
    //       script.type = 'text/javascript';
    //       script.src = src;
    //       script.async = true;
    //       script.onload = () => {
    //         script.dataset.loaded = 'true';
    //         resolve();
    //       };
    //       script.onerror = () => reject(new Error(`Script failed to load: ${src}`));
    //       document.body.appendChild(script);
    //     });
    //   }
}
